import * as React from "react";

import type { AnalyticsEventParameters } from "hooks/useAnalytics";
import { useAnalytics } from "hooks/useAnalytics";

/**
 * This hook is responsible for checking the visibility of the new window
 * (eg: GitHub App install page), and the current document state
 */
export const useNewControlledWindow = ({
  url,
  trackEvents,
}: {
  url: string;
  trackEvents: {
    open: AnalyticsEventParameters[0];
    close: AnalyticsEventParameters[0];
  };
}): {
  currentDocFocused: boolean;
  newWindowOpened: boolean;
  openNewWindow: () => void;
  resetWindowControl: () => void;
} => {
  const track = useAnalytics();
  const popupRef = React.useRef<Window>(null);
  const [state, setState] = React.useState({
    currentDocFocused: false,
    newWindowOpened: false,
  });

  React.useEffect(
    function applyBodyStyle() {
      const body = window.document.querySelector("body");
      if (!body) return;

      if (state.newWindowOpened && !state.currentDocFocused) {
        body.style.filter = `grayscale(80%)`;
      } else {
        body.style.filter = ``;
      }

      return () => {
        body.style.filter = ``;
      };
    },
    [state],
  );

  React.useEffect(function checkPageState() {
    let refAnimation: number;
    let timeout: NodeJS.Timeout;

    const checkWindowState = () => {
      setState((prev) => ({
        ...prev,
        currentDocFocused: document.hasFocus(),
      }));

      if (popupRef.current) {
        if (popupRef.current.closed) {
          /**
           * @fix-me: First parameter of the tuple union (AnalyticsEventParameters[0])
           * didn't match the target (track) parameter type, althought is type-safe along the way
           */
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          track(trackEvents.close as any, {
            event_source: "editor",
          });

          setState((prev) => ({
            ...prev,
            newWindowOpened: false,
          }));
        }
      }

      timeout = setTimeout(() => {
        refAnimation = requestAnimationFrame(checkWindowState);
      }, 300);
    };

    checkWindowState();

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(refAnimation);
    };
  }, []);

  return {
    ...state,
    openNewWindow: () => {
      const popupW = 570;
      const popupH = 770;

      const windowRef = window.open(
        url,
        "mywindow",
        `menubar=1,resizable=1,width=${popupW},height=${popupH},top=${
          window.screen.height / 2 - popupH / 2
        },left=${window.screen.width / 2 - popupW / 2}`,
      );

      /**
       * @fix-me: First parameter of the tuple union (AnalyticsEventParameters[0])
       * didn't match the target (track) parameter type, althought is type-safe along the way
       */
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      track(trackEvents.open as any, {
        event_source: "editor",
      });

      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      (popupRef.current as any) = windowRef;
      setState((prev) => ({
        ...prev,
        newWindowOpened: true,
      }));
    },
    resetWindowControl: () =>
      setState({
        currentDocFocused: false,
        newWindowOpened: false,
      }),
  };
};
